/**
 * Update indicator (top-right of the Header).
 *
 * Visibility rules — this component returns `null` unless one of these
 * status kinds is active:
 *   - update_available  → "Update available" button (click to open popover)
 *   - downloading       → progress label
 *   - installing        → "Installing…"
 *   - ready             → "Restart to update"
 *   - error             → small "Update failed" pill (click to retry check)
 *
 * The states `idle`, `checking`, `up_to_date`, and `needs_reauth` are
 * intentionally hidden from the top bar. The Settings → Updates section
 * surfaces those for users who want to check on demand.
 */

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useUpdateStore } from '@/shared/stores'
import { IS_TAURI } from '@/lib/platform'

/**
 * Restart the desktop app after an update installs (Task 4093).
 *
 * Lazily imported so `@tauri-apps/plugin-process` stays out of the browser
 * bundle. Unreachable there in any case: the only path to this button is
 * update status 'ready', which requires the Tauri-gated updater to have run.
 */
function relaunchApp(): void {
  // The IS_TAURI guard is what lets Rollup drop `plugin-process` from the web
  // bundle. An unguarded dynamic import is still emitted as a chunk, because
  // the bundler cannot prove the call is unreachable — being behind a build-time
  // constant is the whole mechanism (Task 4093).
  if (!IS_TAURI) return
  void import('@tauri-apps/plugin-process')
    .then(({ relaunch }) => relaunch())
    .catch(() => { /* handled by Tauri's own error UI */ })
}

export function UpdateIndicator() {
  const status = useUpdateStore(s => s.status)
  const downloadAndInstall = useUpdateStore(s => s.downloadAndInstall)
  const checkForUpdate = useUpdateStore(s => s.checkForUpdate)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Close the popover on outside-click.
  useEffect(() => {
    if (!popoverOpen) return
    function onMouseDown(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setPopoverOpen(false)
      }
    }
    window.addEventListener('mousedown', onMouseDown)
    return () => window.removeEventListener('mousedown', onMouseDown)
  }, [popoverOpen])

  // We deliberately do NOT auto-open the popover when an update appears.
  // The brief asks for a subtle, always-there pill the operator notices on
  // their own — popping a dialog open unprompted is the opposite of subtle.
  // The operator clicks the pill to see details and install.

  // Hidden states — nothing to render.
  if (
    status.kind === 'idle' ||
    status.kind === 'checking' ||
    status.kind === 'up_to_date' ||
    status.kind === 'needs_reauth'
  ) {
    return null
  }

  // ── Trigger label + tone per state ─────────────────────────────────────
  let label = ''
  let tone: 'accent' | 'progress' | 'success' | 'error' = 'accent'
  switch (status.kind) {
    case 'update_available':
      label = `Update ${status.latestVersion}`
      tone = 'accent'
      break
    case 'downloading': {
      const pct =
        status.totalBytes > 0
          ? Math.round((status.bytesDownloaded / status.totalBytes) * 100)
          : 0
      label = `Downloading ${pct}%`
      tone = 'progress'
      break
    }
    case 'installing':
      label = 'Installing…'
      tone = 'progress'
      break
    case 'ready':
      label = 'Restart to update'
      tone = 'success'
      break
    case 'error':
      label = 'Update failed'
      tone = 'error'
      break
  }

  const triggerStyle: CSSProperties = {
    ...styles.trigger,
    ...(tone === 'accent' ? styles.triggerAccent : {}),
    ...(tone === 'progress' ? styles.triggerProgress : {}),
    ...(tone === 'success' ? styles.triggerSuccess : {}),
    ...(tone === 'error' ? styles.triggerError : {}),
  }

  return (
    <div ref={containerRef} style={styles.container}>
      <button
        type="button"
        style={triggerStyle}
        onClick={() => setPopoverOpen(o => !o)}
        title="Update available"
        aria-expanded={popoverOpen}
        aria-haspopup="dialog"
        data-testid="header-update-indicator"
      >
        <span style={styles.dot} aria-hidden />
        <span style={styles.triggerLabel}>{label}</span>
      </button>

      {popoverOpen && (
        <div style={styles.popover} role="dialog" aria-label="Update details">
          <UpdatePopoverBody
            status={status}
            onInstall={() => {
              setPopoverOpen(false)
              void downloadAndInstall()
            }}
            onRestart={() => {
              relaunchApp()
            }}
            onRetry={() => {
              void checkForUpdate()
            }}
          />
        </div>
      )}
    </div>
  )
}

// ── Popover body ───────────────────────────────────────────────────────────

interface UpdatePopoverBodyProps {
  status: Exclude<
    ReturnType<typeof useUpdateStore.getState>['status'],
    | { kind: 'idle' }
    | { kind: 'checking' }
    | { kind: 'up_to_date' }
    | { kind: 'needs_reauth' }
  >
  onInstall: () => void
  onRestart: () => void
  onRetry: () => void
}

function UpdatePopoverBody({
  status,
  onInstall,
  onRestart,
  onRetry,
}: UpdatePopoverBodyProps) {
  switch (status.kind) {
    case 'update_available': {
      const sizeMb =
        status.fileSizeBytes !== null
          ? (status.fileSizeBytes / (1024 * 1024)).toFixed(1)
          : null
      return (
        <>
          <p style={styles.popoverTitle}>Update available</p>
          <p style={styles.popoverDetail}>
            Version {status.latestVersion}
            {sizeMb ? ` · ${sizeMb} MB` : ''}
          </p>
          <p style={styles.popoverDetailMuted}>
            You are on {status.currentVersion}
          </p>
          <button style={styles.primaryButton} onClick={onInstall}>
            Download and install
          </button>
        </>
      )
    }

    case 'downloading': {
      const pct =
        status.totalBytes > 0
          ? Math.round((status.bytesDownloaded / status.totalBytes) * 100)
          : 0
      const downloadedMb = (status.bytesDownloaded / (1024 * 1024)).toFixed(1)
      const totalMb =
        status.totalBytes > 0
          ? (status.totalBytes / (1024 * 1024)).toFixed(1)
          : '…'
      return (
        <>
          <p style={styles.popoverTitle}>Downloading update</p>
          <p style={styles.popoverDetailMuted}>
            {downloadedMb} / {totalMb} MB
          </p>
          <div style={styles.progressTrack}>
            <div style={{ ...styles.progressFill, width: `${pct}%` }} />
          </div>
        </>
      )
    }

    case 'installing':
      return (
        <>
          <p style={styles.popoverTitle}>Installing update</p>
          <p style={styles.popoverDetailMuted}>
            Approve the macOS permission prompt to overwrite /Applications.
          </p>
        </>
      )

    case 'ready':
      return (
        <>
          <p style={styles.popoverTitle}>Update installed</p>
          <p style={styles.popoverDetail}>
            Restart Kodeon Stream to run version {status.latestVersion}.
          </p>
          <button style={styles.primaryButton} onClick={onRestart}>
            Restart now
          </button>
        </>
      )

    case 'error':
      return (
        <>
          <p style={styles.popoverTitle}>Update failed</p>
          <p style={styles.popoverDetailError}>{status.message}</p>
          <button style={styles.secondaryButton} onClick={onRetry}>
            Try again
          </button>
        </>
      )
  }
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    position: 'relative',
  },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--surface-elevated-strong)',
    padding: '4px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    transition: 'all 200ms ease',
  },
  triggerAccent: {
    color: 'var(--accent-primary, #60a5fa)',
    border: '1px solid rgba(96, 165, 250, 0.3)',
    background: 'rgba(96, 165, 250, 0.08)',
  },
  triggerProgress: {
    color: 'var(--text-secondary)',
  },
  triggerSuccess: {
    color: 'var(--success, #22c55e)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    background: 'rgba(34, 197, 94, 0.08)',
  },
  triggerError: {
    color: 'var(--error, #ef4444)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    background: 'rgba(239, 68, 68, 0.08)',
  },
  triggerLabel: {
    whiteSpace: 'nowrap',
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: 'currentColor',
    boxShadow: '0 0 6px currentColor',
  },

  popover: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    minWidth: '260px',
    padding: '12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-primary)',
    boxShadow: 'var(--shadow-popover, 0 8px 24px rgba(0,0,0,0.25))',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    zIndex: 50,
  },
  popoverTitle: {
    margin: 0,
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  popoverDetail: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-primary)',
  },
  popoverDetailMuted: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  popoverDetailError: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--error, #ef4444)',
    wordBreak: 'break-word',
  },
  primaryButton: {
    marginTop: '4px',
    padding: '6px 12px',
    borderRadius: 'var(--radius-sm)',
    border: 'none',
    background: 'var(--accent-primary, #60a5fa)',
    color: 'white',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  secondaryButton: {
    marginTop: '4px',
    padding: '6px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  progressTrack: {
    height: '6px',
    borderRadius: '3px',
    background: 'var(--bg-secondary)',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '3px',
    background: 'var(--accent-primary, #60a5fa)',
    transition: 'width 0.2s ease',
  },
}
